import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Calculator, CheckCircle2, GitBranch, Layers, Pencil, Plus, Trash2
} from 'lucide-react';
import {
  BOM_RULE_LABELS, BOM_RULE_TYPES, PERMISSIONS, formatMoney, formatQty, type Paginated
} from '@erp/shared';
import { api, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState, DetailRow, ProgressBar } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { BomLineEditor, type EditableBomLine } from './BomLineEditor';
import { ModelEditPanel, type EditableModel } from './ModelEditPanel';

interface ModelOption { id: string; code: string; name: string }
interface ModelVersion { id: string; version: number; status: string; line_count: number }

interface BomLine {
  id: string; productId: string; sku: string; name: string; ruleType: string;
  fixedQty: number; formula: string | null; wastePct: number; isOptional: boolean;
  optionCode: string | null; unitKind: string; unitCode: string; baseUnitId: string;
  cost: number; notes: string | null;
  alternatives: { productId: string; sku: string; name: string }[];
}

interface BomVersion {
  id: string; version: number; status: string; modelId: string; modelCode: string;
  modelName: string; widthMm: number; heightMm: number; powerW: number | null; lines: BomLine[];
}

interface ModelDetail {
  id: string; code: string; name: string; shape: string; width_mm: number; height_mm: number;
  thickness_mm: number; led_type: string | null; kelvin: number | null; power_w: number | null;
  active: boolean; packaging_mode: string; packaging_product_id: string | null;
  description: string | null; options: ModelOption[]; versions: ModelVersion[];
  familyId?: string | null; notes?: string | null; isCustom?: boolean;
  activeBom: BomVersion | null;
  units: { id: string; qr_code: string; serial: number; status: string; created_at: string }[];
}

interface PreviewResult {
  model: { code: string; name: string; widthMm: number; heightMm: number };
  lines: { productId: string; sku: string; name: string; quantity: number; unitCode: string; cost: number; ruleType: string; wastePct: number }[];
  totalCost: number;
}

export function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [lineOpen, setLineOpen] = useState(false);
  const [lineEditing, setLineEditing] = useState<EditableBomLine | null>(null);
  const [modelOpen, setModelOpen] = useState(false);

  // Si se cambia de versión con el editor abierto, guardar daría 409 BOM_LOCKED: se cierra.
  useEffect(() => { setLineEditing(null); }, [selectedVersion]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const modelQuery = useQuery({
    queryKey: ['model', id],
    queryFn: () => api<ModelDetail>(`/mirror-models/${id}`),
    enabled: Boolean(id)
  });

  const model = modelQuery.data;

  useEffect(() => {
    if (!model) return;
    if (selectedVersion && model.versions.some((v) => v.id === selectedVersion)) return;
    setSelectedVersion(model.activeBom?.id ?? model.versions[0]?.id ?? '');
  }, [model, selectedVersion]);

  const bomQuery = useQuery({
    queryKey: ['bom', selectedVersion],
    queryFn: () => api<BomVersion>(`/bom-versions/${selectedVersion}`),
    enabled: Boolean(selectedVersion)
  });

  const productsQuery = useQuery({
    queryKey: ['products-options'],
    queryFn: () => api<Paginated<{ id: string; sku: string; name: string; cost: number; base_unit: string }>>('/products?pageSize=200&active=true')
  });

  const bom = bomQuery.data;
  const selectedVersionRow = model?.versions.find((v) => v.id === selectedVersion);
  const canWrite = can('bom.write');
  const canEditModel = can(PERMISSIONS.MIRRORS_WRITE);
  // El simulador y el listado leen la ficha en snake_case; la fila completa viene también en
  // camelCase, que es lo que se manda de vuelta al editar.
  const modeloEditable = useMemo<EditableModel | null>(() => (model ? {
    id: model.id, code: model.code, name: model.name,
    familyId: model.familyId ?? null, description: model.description,
    shape: model.shape, widthMm: model.width_mm, heightMm: model.height_mm,
    thicknessMm: model.thickness_mm, ledType: model.led_type, kelvin: model.kelvin,
    powerW: model.power_w, isCustom: model.isCustom ?? false, active: model.active,
    notes: model.notes ?? null
  } : null), [model]);

  const newVersion = useMutation({
    mutationFn: () => api<{ id: string }>(`/mirror-models/${id}/bom-versions`, {
      // Se copia **la versión que se está viendo**: si hay un borrador a medias, la nueva versión
      // tiene que partir de él y no de la activa, o se pierde lo que el usuario llevaba hecho.
      method: 'POST', body: { copyFromVersionId: selectedVersion || model?.activeBom?.id }
    }),
    onSuccess: async (created) => {
      toast.success('Borrador creado a partir de esta versión. Ya se puede editar.');
      await qc.invalidateQueries({ queryKey: ['model', id] });
      setSelectedVersion(created.id);
    },
    onError: (error) => toast.error(errorMessage(error))
  });

  const activate = useMutation({
    mutationFn: () => api(`/bom-versions/${selectedVersion}/activate`, { method: 'POST' }),
    onSuccess: async () => {
      toast.success('BOM activada. Los pedidos anteriores conservan su versión.');
      await qc.invalidateQueries({ queryKey: ['model', id] });
      await qc.invalidateQueries({ queryKey: ['bom', selectedVersion] });
      await qc.invalidateQueries({ queryKey: ['models'] });
    },
    onError: (error) => toast.error(errorMessage(error))
  });

  const deleteLine = useMutation({
    mutationFn: (lineId: string) => api(`/bom-lines/${lineId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast.success('Línea eliminada');
      await qc.invalidateQueries({ queryKey: ['bom', selectedVersion] });
      await qc.invalidateQueries({ queryKey: ['model', id] });
    },
    onError: (error) => toast.error(errorMessage(error))
  });

  const runPreview = async (width: number, height: number, quantity: number, configuration: Record<string, unknown>) => {
    setPreviewLoading(true);
    try {
      const result = await api<PreviewResult>(`/bom-versions/${selectedVersion}/preview`, {
        method: 'POST', body: { widthMm: width, heightMm: height, quantity, configuration }
      });
      setPreview(result);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  };

  if (modelQuery.error) return <ErrorState message={(modelQuery.error as Error).message} onRetry={() => void modelQuery.refetch()} />;
  if (modelQuery.isLoading || !model) {
    return <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}</div>;
  }

  const bomCost = bom?.lines.reduce((acc, line) => acc + (line.ruleType === 'fixed' ? line.fixedQty * line.cost : 0), 0) ?? 0;

  return (
    <div>
      <div className="mb-3">
        <Link to="/models" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3" /> Modelos</Link>
      </div>
      <PageHeader
        title={model.name}
        badge={<><StatusBadge status={model.active ? 'active' : 'inactive'} /> <Badge tone="outline" className="font-mono">{model.code}</Badge></>}
        description={model.description ?? 'Ficha técnica y composición versionada'}
        actions={(
          <>
            {canEditModel ? (
              <Button variant="outline" size="sm" onClick={() => setModelOpen(true)}><Pencil /> Editar modelo</Button>
            ) : null}
            <Link to="/models"><Button variant="outline" size="sm">Volver</Button></Link>
            <Badge tone="info"><Layers className="size-3" /> {model.units.length} unidades registradas</Badge>
          </>
        )}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card><CardContent className="p-3"><p className="label-xs">Geometría</p><p className="mt-1 text-sm font-semibold">{model.width_mm} × {model.height_mm} mm</p><p className="text-[11px] text-muted-foreground">{model.shape} · {model.thickness_mm} mm</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="label-xs">Iluminación</p><p className="mt-1 text-sm font-semibold">{model.led_type ?? '—'}</p><p className="text-[11px] text-muted-foreground">{model.kelvin ? `${model.kelvin} K` : ''} {model.power_w ? `· ${model.power_w} W` : ''}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="label-xs">Embalaje</p><p className="mt-1 text-sm font-semibold">{model.packaging_mode === 'parametric' ? 'Paramétrico' : 'Fijo'}</p><p className="text-[11px] text-muted-foreground">{model.packaging_product_id ? 'Referencia asignada' : 'Sin asignar'}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="label-xs">Coste fijo</p><p className="mt-1 text-sm font-semibold">{formatMoney(bomCost)}</p><p className="text-[11px] text-muted-foreground">{bom?.lines.length ?? 0} componentes · solo fijos</p></CardContent></Card>
      </div>

      <BomLineEditor
        line={lineEditing}
        open={Boolean(lineEditing)}
        versionId={selectedVersion}
        options={model.options}
        onClose={() => setLineEditing(null)}
        onSaved={() => {
          setLineEditing(null);
          // El simulador se calculó con la composición anterior: se descarta en vez de seguir
          // enseñando un coste viejo.
          setPreview(null);
          void qc.invalidateQueries({ queryKey: ['bom', selectedVersion] });
          void qc.invalidateQueries({ queryKey: ['model', id] });
        }}
      />
      <ModelEditPanel
        model={modeloEditable}
        open={modelOpen}
        onClose={() => setModelOpen(false)}
        onSaved={() => {
          setModelOpen(false);
          void qc.invalidateQueries({ queryKey: ['model', id] });
        }}
      />

      <Tabs defaultValue="bom">
        <TabsList>
          <TabsTrigger value="bom">BOM</TabsTrigger>
          <TabsTrigger value="preview">Simulador</TabsTrigger>
          <TabsTrigger value="units">Unidades</TabsTrigger>
          <TabsTrigger value="options">Opciones</TabsTrigger>
        </TabsList>

        <TabsContent value="bom">
          <AddBomLineModal
            open={lineOpen}
            onOpenChange={setLineOpen}
            versionId={selectedVersion}
            products={productsQuery.data?.data ?? []}
            options={model.options}
            onSaved={() => {
              setLineOpen(false);
              void qc.invalidateQueries({ queryKey: ['bom', selectedVersion] });
              void qc.invalidateQueries({ queryKey: ['model', id] });
            }}
          />
          <Card className={lineOpen ? 'hidden' : ''}>
            <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
              <Select value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)} className="w-auto min-w-48">
                {model.versions.map((version) => (
                  <option key={version.id} value={version.id}>v{version.version} · {version.status} · {version.line_count} líneas</option>
                ))}
              </Select>
              {selectedVersionRow ? <StatusBadge status={selectedVersionRow.status} /> : null}
              <div className="ml-auto flex flex-wrap gap-2">
                {canWrite ? <Button variant="outline" size="sm" onClick={() => newVersion.mutate()} loading={newVersion.isPending}><GitBranch /> Nueva versión</Button> : null}
                {canWrite && selectedVersionRow?.status === 'draft' ? (
                  <Button size="sm" onClick={() => activate.mutate()} loading={activate.isPending}><CheckCircle2 /> Activar BOM</Button>
                ) : null}
                {canWrite && selectedVersionRow?.status === 'draft' ? (
                  <Button size="sm" onClick={() => setLineOpen(true)}><Plus /> Añadir línea</Button>
                ) : null}
              </div>
            </div>
            {selectedVersionRow && selectedVersionRow.status !== 'draft' ? (
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
                <span>
                  Esta versión está {selectedVersionRow.status === 'active' ? 'activa' : 'retirada'}: la usan los
                  pedidos y los kits, así que no se puede cambiar. Duplícala para editar la composición.
                </span>
                {canWrite ? (
                  <Button variant="outline" size="sm" className="ml-auto" onClick={() => newVersion.mutate()} loading={newVersion.isPending}>
                    <GitBranch /> Duplicar para editar
                  </Button>
                ) : null}
              </div>
            ) : null}
            {bomQuery.isLoading ? <div className="p-4"><ProgressBar value={60} /></div> : !bom ? <EmptyState title="Sin BOM" /> : (
              <div className="overflow-x-auto">
                <table className="table-dense w-full min-w-[860px]">
                  <thead className="border-b border-border bg-muted/30">
                    <tr><th>Componente</th><th>Regla</th><th className="text-right">Cantidad</th><th className="text-right">Merma</th><th>Condición</th><th className="text-right">Coste</th><th></th></tr>
                  </thead>
                  <tbody>
                    {bom.lines.map((line) => (
                      <tr key={line.id}>
                        <td>
                          <p className="font-medium">{line.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{line.sku}{line.alternatives.length ? ` · alt: ${line.alternatives.map((a) => a.sku).join(', ')}` : ''}</p>
                        </td>
                        <td><Badge tone="outline">{BOM_RULE_LABELS[line.ruleType as keyof typeof BOM_RULE_LABELS] ?? line.ruleType}</Badge></td>
                        <td className="text-right tabular-nums">
                          {line.ruleType === 'fixed' ? `${formatQty(line.fixedQty)} ${line.unitCode}` : line.formula ? <span className="font-mono text-[11px]">{line.formula}</span> : '—'}
                        </td>
                        <td className="text-right tabular-nums">{line.wastePct}%</td>
                        <td>{line.isOptional ? <Badge tone="warning">Opcional {line.optionCode ? `· ${line.optionCode}` : ''}</Badge> : <span className="text-[11px] text-muted-foreground">Obligatorio</span>}</td>
                        <td className="text-right tabular-nums">{formatMoney(line.cost)}</td>
                        <td>
                          {canWrite && selectedVersionRow?.status === 'draft' ? (
                            <span className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon-sm" title="Editar línea"
                                onClick={() => setLineEditing(line)}>
                                <Pencil />
                              </Button>
                              <Button variant="ghost" size="icon-sm" title="Quitar de la BOM"
                                onClick={() => deleteLine.mutate(line.id)}><Trash2 /></Button>
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <PreviewPanel model={model} loading={previewLoading} result={preview} onRun={runPreview} />
        </TabsContent>

        <TabsContent value="units">
          <Card>
            <CardHeader title="Unidades del modelo" description="QR internos de fabricación" />
            <div className="overflow-x-auto">
              <table className="table-dense w-full">
                <thead className="border-b border-border bg-muted/30"><tr><th>QR</th><th>Serie</th><th>Estado</th><th>Creada</th><th></th></tr></thead>
                <tbody>
                  {model.units.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-xs text-muted-foreground">Sin unidades</td></tr> : model.units.map((unit) => (
                    <tr key={unit.id}>
                      <td className="font-mono text-xs">{unit.qr_code}</td>
                      <td>{unit.serial}</td>
                      <td><StatusBadge status={unit.status} /></td>
                      <td className="text-[11px] text-muted-foreground">{new Date(unit.created_at).toLocaleString('es-ES')}</td>
                      <td><Button variant="ghost" size="sm" onClick={() => void openPdf(`/mirror-units/${unit.id}/label.pdf`)}>Etiqueta</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="options">
          <Card>
            <CardHeader title="Opciones configurables" description="Activan líneas opcionales de la BOM" />
            <div className="divide-y divide-border">
              {model.options.length === 0 ? <p className="p-6 text-xs text-muted-foreground">Sin opciones definidas.</p> : model.options.map((option) => (
                <div key={option.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div><p className="font-medium">{option.name}</p><p className="font-mono text-[10px] text-muted-foreground">{option.code}</p></div>
                  <Badge tone="outline">boolean</Badge>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}

function PreviewPanel({ model, loading, result, onRun }: {
  model: ModelDetail; loading: boolean; result: PreviewResult | null;
  onRun: (width: number, height: number, quantity: number, configuration: Record<string, unknown>) => Promise<void>;
}) {
  const [width, setWidth] = useState(String(model.width_mm));
  const [height, setHeight] = useState(String(model.height_mm));
  const [quantity, setQuantity] = useState('1');
  const [configuration, setConfiguration] = useState<Record<string, boolean>>({});
  const total = useMemo(() => result?.totalCost ?? 0, [result]);

  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      <Card>
        <CardHeader title="Parámetros" description="Calcula necesidades para una configuración" />
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Ancho (mm)"><Input type="number" value={width} onChange={(e) => setWidth(e.target.value)} /></Field>
            <Field label="Alto (mm)"><Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} /></Field>
          </div>
          <Field label="Cantidad"><Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
          <div>
            <p className="mb-1.5 text-xs font-medium">Opciones</p>
            <div className="space-y-2">
              {model.options.map((option) => (
                <label key={option.code} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={Boolean(configuration[option.code])} onChange={(e) => setConfiguration((current) => ({ ...current, [option.code]: e.target.checked }))} />
                  {option.name}
                </label>
              ))}
              {model.options.length === 0 ? <p className="text-[11px] text-muted-foreground">Sin opciones</p> : null}
            </div>
          </div>
          <Button className="w-full" loading={loading} onClick={() => void onRun(Number(width), Number(height), Number(quantity) || 1, configuration)}><Calculator /> Calcular BOM</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Necesidades calculadas" description={result ? `${result.model.name} · ${result.model.widthMm}×${result.model.heightMm} mm` : 'Ejecuta el simulador para ver el desglose'} actions={result ? <Badge tone="success">{formatMoney(total)}</Badge> : null} />
        {!result ? (
          <EmptyState title="Sin simulación" description="Introduce medidas y pulsa Calcular BOM." icon={Calculator} />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-dense w-full">
              <thead className="border-b border-border bg-muted/30"><tr><th>Componente</th><th>Regla</th><th className="text-right">Cantidad</th><th className="text-right">Coste</th></tr></thead>
              <tbody>
                {result.lines.map((line) => (
                  <tr key={line.productId}>
                    <td><p className="font-medium">{line.name}</p><p className="font-mono text-[10px] text-muted-foreground">{line.sku}</p></td>
                    <td><Badge tone="outline">{BOM_RULE_LABELS[line.ruleType as keyof typeof BOM_RULE_LABELS] ?? line.ruleType}</Badge></td>
                    <td className="text-right tabular-nums">{formatQty(line.quantity)} {line.unitCode}</td>
                    <td className="text-right tabular-nums">{formatMoney(line.cost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-border font-semibold"><td colSpan={3} className="px-3 py-2 text-right text-xs">Coste material</td><td className="px-3 py-2 text-right text-xs">{formatMoney(total)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function AddBomLineModal({ open, onOpenChange, versionId, products, options, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; versionId: string;
  products: { id: string; sku: string; name: string; cost: number; base_unit: string }[];
  options: ModelOption[]; onSaved: () => void;
}) {
  const [productId, setProductId] = useState('');
  const [ruleType, setRuleType] = useState('fixed');
  const [fixedQty, setFixedQty] = useState('1');
  const [formula, setFormula] = useState('');
  const [waste, setWaste] = useState('0');
  const [isOptional, setIsOptional] = useState(false);
  const [optionCode, setOptionCode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProductId(''); setRuleType('fixed'); setFixedQty('1'); setFormula(''); setWaste('0');
    setIsOptional(false); setOptionCode('');
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (!productId) throw new Error('Selecciona un componente');
      await api(`/bom-versions/${versionId}/lines`, {
        method: 'POST',
        body: {
          productId, ruleType, fixedQty: Number(fixedQty) || 0, formula: formula || null,
          wastePct: Number(waste) || 0, isOptional, optionCode: optionCode || null
        }
      });
      toast.success('Línea añadida');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="md" title="Añadir componente a la BOM"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Añadir</Button></>}
    >
      <div className="space-y-3">
        <Field label="Componente" required>
          <Select value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">Selecciona…</option>
            {products.map((product) => <option key={product.id} value={product.id}>{product.sku} · {product.name}</option>)}
          </Select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Regla de cálculo">
            <Select value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
              {BOM_RULE_TYPES.map((rule) => <option key={rule} value={rule}>{BOM_RULE_LABELS[rule]}</option>)}
            </Select>
          </Field>
          <Field label="Merma (%)"><Input type="number" step="0.01" min="0" value={waste} onChange={(e) => setWaste(e.target.value)} /></Field>
        </div>
        {ruleType === 'fixed' ? (
          <Field label="Cantidad fija"><Input type="number" step="0.001" value={fixedQty} onChange={(e) => setFixedQty(e.target.value)} /></Field>
        ) : (
          <Field label="Fórmula" hint="Variables: width, height, perimeter, area, power, qty. Ej.: PI*width">
            <Input value={formula} onChange={(e) => setFormula(e.target.value)} placeholder="perimeter * 1.05" className="font-mono" />
          </Field>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 pb-2 text-xs"><input type="checkbox" checked={isOptional} onChange={(e) => setIsOptional(e.target.checked)} /> Solo si opción activa</label>
          {isOptional ? (
            <Field label="Opción" className="flex-1">
              <Select value={optionCode} onChange={(e) => setOptionCode(e.target.value)}>
                <option value="">Selecciona…</option>
                {options.map((option) => <option key={option.code} value={option.code}>{option.name}</option>)}
              </Select>
            </Field>
          ) : null}
        </div>
      </div>
    </InlineView>
  );
}
